import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGS, NON_EN_CODES } from "../i18n";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Globe } from "lucide-react";

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const switchLanguage = (newLang) => {
    const segments = location.pathname.split("/").filter(Boolean);
    const hasPrefix = NON_EN_CODES.includes(segments[0]);
    const basePath = hasPrefix
      ? "/" + segments.slice(1).join("/") || "/"
      : location.pathname;

    i18n.changeLanguage(newLang);
    // Explicit user choice — always persist, including English
    try { localStorage.setItem("i18n-lang", newLang); } catch {}

    const target =
      newLang === "en"
        ? `${basePath}${location.search}${location.hash}`
        : `/${newLang}${basePath === "/" ? "" : basePath}${location.search}${location.hash}`;

    navigate(target);
  };

  const current = SUPPORTED_LANGS.find((l) => l.code === i18n.language) || SUPPORTED_LANGS[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-slate-100 transition-colors text-sm"
          data-testid="language-switcher"
        >
          <Globe className="h-4 w-4 text-slate-500" />
          <span className="text-slate-700 font-medium text-xs uppercase">{current.code}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {SUPPORTED_LANGS.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => switchLanguage(lang.code)}
            data-testid={`lang-option-${lang.code}`}
            className={`cursor-pointer ${lang.code === i18n.language ? "bg-teal-50 text-teal-700" : ""}`}
          >
            <span className="font-medium mr-2 uppercase text-xs w-5">{lang.code}</span>
            {lang.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
